/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { sharedAdminuiAppApi } from '../../../ServiceFactory';
import { getCurrentPage, getNextPage } from '../helpers';
import { useStore } from '../store';
import { App, AppVariant, ListType } from '../types';

const useFetchAppVariant = (tenantIds: string[], search?: string): UseInfiniteQueryResult<App[]> => {
	const { setCurrentListPage, currentAppVariantPage } = useStore();
	return useInfiniteQuery<App[]>(
		[ListType.APP_VARIANT, { tenantIds, search }],
		({ pageParam = 0 }) =>
			sharedAdminuiAppApi()
				.getAppVariantsByTenantOrContract({ count: 500, tenantIds, page: pageParam, search })
				.then(response => response)
				.then(data => {
					const { content, page } = data;
					const { totalPages, number: responsePage } = page || {};
					const items: { [key: string]: App } = {};
					content?.forEach(({ app, ...childData }) => {
						if (app?.id) {
							if (!items[app.id]) {
								items[app.id] = app as App;
								items[app.id].subItems = [];
							}

							items[app.id].subItems?.push(childData as AppVariant);
						}
					});
					const processedItems = Object.values(items);
					const currentPage = getCurrentPage(responsePage, totalPages);
					setCurrentListPage(ListType.APP_VARIANT, currentPage);
					return processedItems;
				}),
		{
			getNextPageParam: () => getNextPage(currentAppVariantPage)
		}
	);
};

export default useFetchAppVariant;
